import { Card, MicroLabel, Thumb, type QuotePerson } from '@codezo/quote-detail';
import { AlertTriangle, ArrowUpRight, FileText, Lock, Pencil } from 'lucide-react';
import type { ReactNode } from 'react';
import { AdminUserPickerModal } from '../../../components/selectors/AdminUserPickerModal';
import { Button } from '../../../components/ui/button';
import { InitialsAvatar } from '../../../components/ui/initials-avatar';
import { useClientContactsEditor } from '../../quotes/v2/QuoteClientContactsV2';

type Address = {
	addressLine1?: string | null;
	addressLine2?: string | null;
	postalCode?: string | null;
	city?: string | null;
	countryCode?: string | null;
};

/**
 * Everything the order is attached to, as one card in the rail: the client, the
 * quote it came from, where it ships, who runs it and who hears about it.
 *
 * It replaces a stack of separate cards that each drew the same idea — a label,
 * a thing, a way to act on it — in its own shape, with full-size buttons. Here
 * every row is that idea once: a micro label, the content, and an icon action.
 *
 * Managers and client contacts live on the quote — the order's emails resolve
 * recipients from it — so editing them here writes the quote. The address is
 * the order's own snapshot from acceptance: correcting it writes the order and
 * leaves the quote's copy alone, and once anything has shipped the server locks
 * it and the row says why instead of offering the edit.
 */
export function OrderDetailsCard({
	client,
	quote,
	address,
	addressLockedReason,
	addressMismatch,
	onEditAddress,
	managers,
	contacts,
}: {
	client: { name: string; meta: string; onOpen: () => void } | null;
	quote: {
		id: string;
		name: string;
		/** "Deadline 12 Nov · Event". */
		meta?: string | null;
		description?: string | null;
		onOpen: () => void;
	} | null;
	address: Address | null | undefined;
	/** The server's reason the address can't change any more; null while it can. */
	addressLockedReason?: string | null;
	/**
	 * Booked invoices issued before the address was last corrected — they show
	 * the previous one as their ship-to and are not re-issued. Null when every
	 * invoice matches.
	 */
	addressMismatch?: { changedAt: string; invoiceNumbers: number[] } | null;
	onEditAddress: () => void;
	managers: {
		people: { id: string; firstName: string; lastName: string }[];
		selectedIds: string[];
		onSave: (ids: string[]) => void;
		saving: boolean;
		open: boolean;
		onOpenChange: (open: boolean) => void;
	};
	contacts: {
		organizationId: string | null | undefined;
		clientContactUserIds: string[];
		recipientsVersion: number;
		onChanged: () => void;
	};
}) {
	const addressLines = address
		? [
				address.addressLine1,
				address.addressLine2,
				[address.postalCode, address.city].filter(Boolean).join(' '),
				address.countryCode,
			].filter((line): line is string => !!line)
		: [];

	return (
		<Card>
			<Row
				label="Client"
				action={client && <IconAction label="Open client" onClick={client.onOpen} open />}
			>
				{client ? (
					<Entity
						avatar={<InitialsAvatar name={client.name} size={26} />}
						name={client.name}
						meta={client.meta}
					/>
				) : (
					<Muted>This order isn’t attached to a client.</Muted>
				)}
			</Row>

			{quote && (
				<Row
					label="From quote"
					action={<IconAction label="Open quote" onClick={quote.onOpen} open />}
				>
					<Entity
						avatar={
							<Thumb size={26}>
								<FileText size={12} />
							</Thumb>
						}
						name={quote.name}
						meta={quote.meta}
					/>
					{quote.description && (
						<p className="mt-2 line-clamp-2 text-sm text-text-3">{quote.description}</p>
					)}
				</Row>
			)}

			<Row
				label="Ships to"
				action={
					addressLockedReason ? (
						<Lock size={12} className="text-text-4" aria-label={addressLockedReason}>
							<title>{addressLockedReason}</title>
						</Lock>
					) : (
						<IconAction label="Edit delivery address" onClick={onEditAddress} />
					)
				}
			>
				{addressLines.length > 0 ? (
					<div className="text-sm leading-relaxed text-text-1">
						{addressLines.map((line) => (
							<div key={line}>{line}</div>
						))}
					</div>
				) : (
					<Muted>No address was recorded when this order was placed.</Muted>
				)}
				{addressMismatch && (
					<div className="mt-2 flex items-start gap-1.5 text-xs text-status-warn">
						<AlertTriangle size={12} className="mt-px shrink-0" />
						<span>
							Changed {addressMismatch.changedAt}.{' '}
							{addressMismatch.invoiceNumbers.length === 1
								? `Invoice #${addressMismatch.invoiceNumbers[0]} shows the previous address.`
								: `Invoices ${addressMismatch.invoiceNumbers
										.map((n) => `#${n}`)
										.join(', ')} show the previous address.`}
						</span>
					</div>
				)}
				{addressLockedReason && (
					<div className="mt-1.5 text-xs text-text-4">{addressLockedReason}</div>
				)}
			</Row>

			{quote && (
				<Row
					label="Managers"
					action={
						<IconAction
							label="Assign managers"
							onClick={() => managers.onOpenChange(true)}
						/>
					}
				>
					<People
						people={managers.people.map((admin) => ({
							id: admin.id,
							name: `${admin.firstName} ${admin.lastName}`,
						}))}
						emptyLabel="None assigned · every admin gets alerts"
					/>
					<AdminUserPickerModal
						open={managers.open}
						onOpenChange={managers.onOpenChange}
						selectedIds={managers.selectedIds}
						onSave={managers.onSave}
						loading={managers.saving}
					/>
				</Row>
			)}

			{quote && contacts.organizationId && (
				<ClientContactsRow
					orderQuoteId={quote.id}
					organizationId={contacts.organizationId}
					organizationName={client?.name}
					clientContactUserIds={contacts.clientContactUserIds}
					recipientsVersion={contacts.recipientsVersion}
					onChanged={contacts.onChanged}
				/>
			)}
		</Card>
	);
}

/** Split out so the contacts hook — and its recipients query — only runs for an order with a client. */
function ClientContactsRow({
	orderQuoteId,
	organizationId,
	organizationName,
	clientContactUserIds,
	recipientsVersion,
	onChanged,
}: {
	orderQuoteId: string;
	organizationId: string;
	organizationName?: string | null;
	clientContactUserIds: string[];
	recipientsVersion: number;
	onChanged: () => void;
}) {
	const { people, openPicker, recipients, picker } = useClientContactsEditor({
		orderQuoteId,
		organizationId,
		clientContactUserIds,
		recipientsVersion,
		onSuccess: onChanged,
	});

	return (
		<Row
			label="Client contacts"
			action={<IconAction label="Choose client contacts" onClick={openPicker} />}
		>
			<People
				people={people}
				emptyLabel={`Nobody chosen · everyone at ${organizationName ?? 'the client'}`}
			/>
			<div className="mt-2.5">{recipients}</div>
			{picker}
		</Row>
	);
}

function Row({
	label,
	action,
	children,
}: {
	label: string;
	action?: ReactNode;
	children: ReactNode;
}) {
	return (
		<div className="border-t border-line-1 px-4 py-3 first:border-t-0">
			<div className="mb-2 flex h-5 items-center justify-between gap-2">
				<MicroLabel>{label}</MicroLabel>
				{action}
			</div>
			{children}
		</div>
	);
}

function Entity({ avatar, name, meta }: { avatar: ReactNode; name: string; meta?: string | null }) {
	return (
		<div className="flex min-w-0 items-center gap-2.5">
			{avatar}
			<div className="min-w-0">
				<div className="truncate text-sm font-medium text-text-1">{name}</div>
				{meta && <div className="truncate text-xs text-text-4">{meta}</div>}
			</div>
		</div>
	);
}

function People({ people, emptyLabel }: { people: QuotePerson[]; emptyLabel: string }) {
	if (people.length === 0) return <Muted>{emptyLabel}</Muted>;
	return (
		<div className="flex flex-col gap-2">
			{people.map((person) => (
				<div key={person.id} className="flex min-w-0 flex-col gap-1">
					<Entity
						avatar={<InitialsAvatar name={person.name} size={22} />}
						name={person.name}
						meta={person.meta}
					/>
					{person.details && <div className="pl-[32px]">{person.details}</div>}
				</div>
			))}
		</div>
	);
}

function Muted({ children }: { children: ReactNode }) {
	return <div className="text-sm text-text-4">{children}</div>;
}

/** Opening something elsewhere shows an arrow; editing it here shows a pencil. */
function IconAction({
	label,
	onClick,
	open,
}: {
	label: string;
	onClick: () => void;
	open?: boolean;
}) {
	return (
		<Button
			variant="ghost"
			size="sm"
			className="-mr-2 h-6 w-6 p-0"
			aria-label={label}
			title={label}
			onClick={onClick}
		>
			{open ? <ArrowUpRight size={13} /> : <Pencil size={12} />}
		</Button>
	);
}
